/* Wild Soul Brand — Custom Ridgeblade gift builder
   - Live preview of name/text in the chosen font (rendered from the real font outlines)
   - Optional logo upload
   - On add to cart, creates a print-ready SVG (text converted to outlines, sized in mm)
     and attaches it + the logo to the order as line item properties, so the order
     email to the print vendor carries everything needed. */
(function () {
  'use strict';

  function initBuilder(root) {
    if (!root || root.dataset.wsbReady) return;
    root.dataset.wsbReady = '1';

    var cfgEl = root.querySelector('[data-gift-config]');
    if (!cfgEl) return;
    var cfg = JSON.parse(cfgEl.textContent);
    var fonts = cfg.fonts;
    var fontCache = {};

    var state = {
      variant: cfg.variants[0],
      mode: 'text',
      line1: '',
      line2: '',
      font: fonts[0],
      color: cfg.colors[0],
      logoFile: null,
      logoUrl: null,
      qty: 1
    };

    var $ = function (sel) { return root.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

    var els = {
      methods: $$('[data-method]'),
      modes: $$('[data-mode]'),
      textFields: $('[data-text-fields]'),
      logoFields: $('[data-logo-fields]'),
      line1: $('[data-line1]'),
      line2: $('[data-line2]'),
      count1: $('[data-count1]'),
      count2: $('[data-count2]'),
      fontBtn: $('[data-font-toggle]'),
      fontBtnLabel: $('[data-font-current]'),
      fontList: $('[data-font-list]'),
      colorWrap: $('[data-color-wrap]'),
      laserNote: $('[data-laser-note]'),
      swatches: $$('[data-color]'),
      colorName: $('[data-color-name]'),
      logoInput: $('[data-logo-input]'),
      logoName: $('[data-logo-name]'),
      logoClear: $('[data-logo-clear]'),
      occasion: $('[data-occasion]'),
      notes: $('[data-notes]'),
      qty: $('[data-qty]'),
      qtyMinus: $('[data-qty-minus]'),
      qtyPlus: $('[data-qty-plus]'),
      unitPrice: $('[data-unit-price]'),
      total: $('[data-total]'),
      breakdown: $('[data-breakdown]'),
      submit: $('[data-submit]'),
      error: $('[data-error]'),
      plate: $('[data-plate]'),
      plateText: $('[data-plate-text]'),
      plateLogo: $('[data-plate-logo]'),
      plateEmpty: $('[data-plate-empty]'),
      methodTag: $('[data-method-tag]')
    };

    function money(cents) {
      var n = (cents / 100).toFixed(2);
      return '$' + n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function isLaser() { return /laser|engrav/i.test(state.variant.title); }

    function setError(msg) {
      els.error.textContent = msg || '';
      els.error.hidden = !msg;
    }

    /* ---------- font loading (real outlines, same data used for the print file) ---------- */
    function loadFont(f) {
      if (fontCache[f.id]) return fontCache[f.id];
      fontCache[f.id] = new Promise(function (resolve, reject) {
        if (!window.opentype) { reject(new Error('opentype not loaded')); return; }
        fetch(f.url).then(function (r) {
          if (!r.ok) throw new Error('font ' + r.status);
          return r.arrayBuffer();
        }).then(function (buf) {
          resolve(window.opentype.parse(buf));
        }).catch(reject);
      });
      return fontCache[f.id];
    }

    /* Build outlined SVG markup for the current text.
       Returns {inner, w, h} in font units, or null when there's no text. */
    function buildTextGeometry(font, lines) {
      var size = 100;
      var gap = size * 0.28;
      var parts = [];
      var maxW = 0, y = 0, totalH = 0;
      lines.forEach(function (text, i) {
        var probe = font.getPath(text, 0, 0, size);
        var bb = probe.getBoundingBox();
        var w = bb.x2 - bb.x1;
        var h = bb.y2 - bb.y1;
        if (!isFinite(w) || w <= 0) return;
        parts.push({ text: text, bb: bb, w: w, h: h });
        maxW = Math.max(maxW, w);
      });
      if (!parts.length) return null;
      totalH = parts.reduce(function (a, p) { return a + p.h; }, 0) + gap * (parts.length - 1);
      var d = '';
      parts.forEach(function (p) {
        var x = (maxW - p.w) / 2 - p.bb.x1;
        var baseline = y - p.bb.y1;
        d += font.getPath(p.text, x, baseline, size).toPathData(2);
        y += p.h + gap;
      });
      return { d: d, w: maxW, h: totalH };
    }

    function currentLines() {
      var l = [];
      if (state.mode === 'logo') return l;
      if (state.line1.trim()) l.push(state.line1.trim());
      if (state.line2.trim()) l.push(state.line2.trim());
      return l;
    }

    function inkColor() { return isLaser() ? '#000000' : state.color.hex; }

    /* ---------- preview ---------- */
    var renderToken = 0;
    function renderPreview() {
      var token = ++renderToken;
      var laser = isLaser();
      els.plate.classList.toggle('is-laser', laser);
      els.plate.classList.toggle('is-uv', !laser);
      els.methodTag.textContent = laser ? 'Laser engraved preview' : 'UV printed preview';

      var showLogo = state.mode !== 'text' && state.logoUrl;
      els.plateLogo.hidden = !showLogo;
      if (showLogo) {
        els.plateLogo.innerHTML = '';
        var img = document.createElement('img');
        img.src = state.logoUrl;
        img.alt = 'Your logo';
        els.plateLogo.appendChild(img);
      } else if (state.mode !== 'text' && state.logoFile) {
        els.plateLogo.hidden = false;
        els.plateLogo.innerHTML = '<span class="wsb-gb__file-chip">' + escapeHtml(state.logoFile.name) + '</span>';
      }

      var lines = currentLines();
      els.plateEmpty.hidden = !!(lines.length || (state.mode !== 'text' && state.logoFile));
      if (!lines.length) { els.plateText.innerHTML = ''; return; }

      // Instant fallback while the outline font loads
      els.plateText.innerHTML = '<div class="wsb-gb__plate-fallback" style="font-family:\'' + state.font.family + '\';color:' + (laser ? '' : state.color.hex) + '">' +
        lines.map(escapeHtml).join('<br>') + '</div>';

      loadFont(state.font).then(function (font) {
        if (token !== renderToken) return;
        var g = buildTextGeometry(font, lines);
        if (!g) return;
        var pad = g.h * 0.08;
        var fill = laser ? 'currentColor' : state.color.hex;
        els.plateText.innerHTML = '<svg class="wsb-gb__plate-svg" viewBox="' + (-pad) + ' ' + (-pad) + ' ' + (g.w + pad * 2) + ' ' + (g.h + pad * 2) +
          '" role="img" aria-label="' + escapeHtml(lines.join(' ')) + '"><path fill="' + fill + '" d="' + g.d + '"/></svg>';
      }).catch(function () { /* fallback text stays */ });
    }

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    /* ---------- print-ready SVG ---------- */
    function makePrintFile() {
      var lines = currentLines();
      if (!lines.length) return Promise.resolve(null);
      return loadFont(state.font).then(function (font) {
        var g = buildTextGeometry(font, lines);
        if (!g) return null;
        var W = cfg.areaW, H = cfg.areaH;
        var scale = Math.min(W / g.w, H / g.h);
        var outW = g.w * scale, outH = g.h * scale;
        var method = isLaser() ? 'Laser engraving' : 'UV print';
        var svg = '<?xml version="1.0" encoding="UTF-8"?>\n' +
          '<!-- Wild Soul Brand custom Ridgeblade | ' + method + ' | Font: ' + state.font.name +
          ' | Text: ' + lines.join(' / ').replace(/--/g, '- -') + ' | Color: ' + (isLaser() ? 'Black (engrave)' : state.color.name + ' ' + state.color.hex) +
          ' | Text converted to outlines. Artwork area ' + W + ' x ' + H + ' mm, design ' + outW.toFixed(1) + ' x ' + outH.toFixed(1) + ' mm -->\n' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="' + outW.toFixed(2) + 'mm" height="' + outH.toFixed(2) + 'mm" viewBox="0 0 ' + g.w.toFixed(2) + ' ' + g.h.toFixed(2) + '">\n' +
          '  <path fill="' + inkColor() + '" d="' + g.d + '"/>\n</svg>\n';
        var safe = lines.join('-').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').slice(0, 40) || 'text';
        var name = 'WSB-' + (isLaser() ? 'LASER' : 'UV') + '-' + safe + '.svg';
        return new File([svg], name, { type: 'image/svg+xml' });
      });
    }

    /* ---------- UI wiring ---------- */
    function selectMethod(id) {
      cfg.variants.forEach(function (v) { if (String(v.id) === String(id)) state.variant = v; });
      els.methods.forEach(function (b) {
        var on = String(b.dataset.method) === String(state.variant.id);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      var laser = isLaser();
      els.colorWrap.hidden = laser;
      els.laserNote.hidden = !laser;
      updatePrice();
      renderPreview();
    }

    function selectMode(mode) {
      state.mode = mode;
      els.modes.forEach(function (b) {
        var on = b.dataset.mode === mode;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      els.textFields.hidden = mode === 'logo';
      els.logoFields.hidden = mode === 'text';
      setError('');
      renderPreview();
    }

    function updatePrice() {
      var unit = state.variant.price;
      els.unitPrice.textContent = money(unit);
      els.total.textContent = money(unit * state.qty);
      var fee = unit - cfg.basePrice;
      els.breakdown.textContent = 'Knife ' + money(cfg.basePrice) + ' + ' + (isLaser() ? 'laser engraving ' : 'UV printing ') + money(fee) +
        (state.qty > 1 ? ' × ' + state.qty : '') + ' · Free shipping';
    }

    function renderFontList() {
      var sample = state.line1.trim() || cfg.sampleText;
      els.fontList.innerHTML = '';
      fonts.forEach(function (f) {
        var li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '-1');
        li.dataset.fontId = f.id;
        li.setAttribute('aria-selected', f.id === state.font.id ? 'true' : 'false');
        li.className = 'wsb-gb__font-opt' + (f.id === state.font.id ? ' is-active' : '');
        li.innerHTML = '<span class="wsb-gb__font-sample" style="font-family:\'' + f.family + '\'">' + escapeHtml(sample) + '</span>' +
          '<span class="wsb-gb__font-name">' + escapeHtml(f.name) + '</span>';
        els.fontList.appendChild(li);
      });
    }

    function setFont(id) {
      fonts.forEach(function (f) { if (f.id === id) state.font = f; });
      els.fontBtnLabel.textContent = state.font.name;
      els.fontBtnLabel.style.fontFamily = "'" + state.font.family + "'";
      closeFonts();
      renderPreview();
    }

    function openFonts() {
      renderFontList();
      els.fontList.hidden = false;
      els.fontBtn.setAttribute('aria-expanded', 'true');
      var active = els.fontList.querySelector('.is-active') || els.fontList.firstChild;
      if (active) active.focus();
    }
    function closeFonts() {
      els.fontList.hidden = true;
      els.fontBtn.setAttribute('aria-expanded', 'false');
    }

    els.methods.forEach(function (b) { b.addEventListener('click', function () { selectMethod(b.dataset.method); }); });
    els.modes.forEach(function (b) { b.addEventListener('click', function () { selectMode(b.dataset.mode); }); });

    function bindLine(input, counter, key) {
      input.addEventListener('input', function () {
        state[key] = input.value;
        counter.textContent = input.value.length + '/' + input.maxLength;
        setError('');
        renderPreview();
      });
    }
    bindLine(els.line1, els.count1, 'line1');
    bindLine(els.line2, els.count2, 'line2');

    els.fontBtn.addEventListener('click', function () {
      if (els.fontList.hidden) openFonts(); else closeFonts();
    });
    els.fontList.addEventListener('click', function (e) {
      var li = e.target.closest('[data-font-id]');
      if (li) { setFont(li.dataset.fontId); els.fontBtn.focus(); }
    });
    els.fontList.addEventListener('keydown', function (e) {
      var items = Array.prototype.slice.call(els.fontList.children);
      var i = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (items[i + 1] || items[0]).focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (items[i - 1] || items[items.length - 1]).focus(); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (items[i]) { setFont(items[i].dataset.fontId); els.fontBtn.focus(); } }
      else if (e.key === 'Escape') { closeFonts(); els.fontBtn.focus(); }
    });
    document.addEventListener('click', function (e) {
      if (!els.fontList.hidden && !e.target.closest('[data-font-picker]')) closeFonts();
    });

    els.swatches.forEach(function (s) {
      s.addEventListener('click', function () {
        cfg.colors.forEach(function (c) { if (c.hex === s.dataset.color) state.color = c; });
        els.swatches.forEach(function (x) {
          var on = x === s;
          x.classList.toggle('is-active', on);
          x.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        els.colorName.textContent = state.color.name;
        renderPreview();
      });
    });

    els.logoInput.addEventListener('change', function () {
      var f = els.logoInput.files && els.logoInput.files[0];
      if (state.logoUrl) { URL.revokeObjectURL(state.logoUrl); state.logoUrl = null; }
      state.logoFile = null;
      if (f) {
        if (f.size > cfg.maxLogoMb * 1024 * 1024) {
          setError('That file is over ' + cfg.maxLogoMb + ' MB. Please upload a smaller file, or email it to us after you order.');
          els.logoInput.value = '';
        } else {
          state.logoFile = f;
          if (/^image\/(png|jpe?g|svg\+xml|webp|gif)$/i.test(f.type)) state.logoUrl = URL.createObjectURL(f);
          setError('');
        }
      }
      els.logoName.textContent = state.logoFile ? state.logoFile.name : 'No file chosen';
      els.logoClear.hidden = !state.logoFile;
      renderPreview();
    });
    els.logoClear.addEventListener('click', function () {
      els.logoInput.value = '';
      els.logoInput.dispatchEvent(new Event('change'));
    });

    function setQty(n) {
      state.qty = Math.max(1, Math.min(999, parseInt(n, 10) || 1));
      els.qty.value = state.qty;
      updatePrice();
    }
    els.qty.addEventListener('change', function () { setQty(els.qty.value); });
    els.qtyMinus.addEventListener('click', function () { setQty(state.qty - 1); });
    els.qtyPlus.addEventListener('click', function () { setQty(state.qty + 1); });

    /* ---------- add to cart ---------- */
    function validate() {
      var lines = currentLines();
      if (state.mode === 'text' && !lines.length) return 'Type the name or text you want on the knife.';
      if (state.mode === 'logo' && !state.logoFile) return 'Upload your logo file.';
      if (state.mode === 'both') {
        if (!state.logoFile) return 'Upload your logo file, or switch to "Name or text".';
        if (!lines.length) return 'Type your text, or switch to "Logo".';
      }
      return '';
    }

    els.submit.addEventListener('click', function () {
      var err = validate();
      if (err) { setError(err); return; }
      setError('');
      els.submit.disabled = true;
      els.submit.classList.add('is-loading');
      var label = els.submit.textContent;
      els.submit.textContent = 'Adding…';

      makePrintFile().catch(function () { return null; }).then(function (printFile) {
        var fd = new FormData();
        fd.append('id', state.variant.id);
        fd.append('quantity', state.qty);
        var lines = currentLines();
        var what = state.mode === 'text' ? 'Name/text' : state.mode === 'logo' ? 'Logo' : 'Logo + text';
        fd.append('properties[Design]', what);
        if (lines[0]) fd.append('properties[Line 1]', lines[0]);
        if (lines[1]) fd.append('properties[Line 2]', lines[1]);
        if (lines.length) fd.append('properties[Font]', state.font.name);
        if (lines.length && !isLaser()) fd.append('properties[Print color]', state.color.name + ' ' + state.color.hex);
        if (state.mode !== 'text' && state.logoFile) fd.append('properties[Logo file]', state.logoFile, state.logoFile.name);
        if (printFile) fd.append('properties[Print-ready text file]', printFile, printFile.name);
        else if (lines.length) fd.append('properties[_Print file]', 'Not generated - set text in ' + state.font.name);
        if (els.occasion.value) fd.append('properties[Occasion]', els.occasion.value);
        if (els.notes.value.trim()) fd.append('properties[Notes]', els.notes.value.trim());

        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          fd.append('sections', drawer.getSectionsToRender().map(function (s) { return s.id; }));
          fd.append('sections_url', window.location.pathname);
        }

        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: fd
        }).then(function (r) { return r.json(); }).then(function (res) {
          if (res.status && res.description) throw new Error(res.description);
          if (drawer && typeof drawer.renderContents === 'function' && res.sections) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(res);
          } else {
            window.location.href = root + 'cart';
          }
        });
      }).catch(function (e) {
        setError('Something went wrong adding this to your cart. ' + (e && e.message ? e.message : 'Please try again.'));
      }).then(function () {
        els.submit.disabled = false;
        els.submit.classList.remove('is-loading');
        els.submit.textContent = label;
      });
    });

    /* init */
    selectMethod(cfg.variants[0].id);
    selectMode('text');
    setFont(fonts[0].id);
    updatePrice();
    // warm the first font
    loadFont(fonts[0]).then(renderPreview).catch(function () {});
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-gift-builder]'), initBuilder);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
